import { useCallback } from 'react';

import { useClearRefinements, useInstantSearch, useRange, useToggleRefinement } from 'react-instantsearch';

import { useTracking } from '@change-corgi/core/react/tracking';

import {
	INSTANT_SEARCH_TRENDING_FILTER,
	INSTANT_SEARCH_TRENDING_FILTER_VALUE,
	INSTANT_SEARCH_VICTORY_FILTER,
} from 'src/app/pages/search/shared/consts';

type TrackingContext = 'status_filter_trending' | 'status_filter_all' | 'status_filter_victories';

type Result = ModelHookResult<
	{
		disabled: boolean;
		isAllPetitionsChecked: boolean;
		isVictoriesOnlyChecked: boolean;
		isTrendingChecked: boolean;
	},
	{
		setAllPetitions: () => void;
		setVictoriesOnly: () => void;
		setTrending: () => void;
		trackStatusFilterClick: (context: TrackingContext) => void;
	}
>;

export function useStatusFilter(): Result {
	const { refine: filterByVictories, value } = useToggleRefinement({
		attribute: INSTANT_SEARCH_VICTORY_FILTER,
		on: 'victory',
		off: undefined,
	});

	const { refine: filterByTrending } = useRange({
		attribute: INSTANT_SEARCH_TRENDING_FILTER,
		precision: 1,
	});

	const { refine: clearRefine } = useClearRefinements({
		includedAttributes: [INSTANT_SEARCH_TRENDING_FILTER, INSTANT_SEARCH_VICTORY_FILTER],
	});

	const { indexUiState, status } = useInstantSearch();

	const track = useTracking();

	const trackStatusFilterClick = useCallback(
		(context: TrackingContext) => {
			void track('search', {
				context,
				q: indexUiState.query || '',
			});
		},
		[indexUiState.query, track],
	);

	return {
		data: {
			disabled: status === 'loading',
			isAllPetitionsChecked: !value.isRefined && !indexUiState.range?.weekly_signature_count,
			isVictoriesOnlyChecked: value.isRefined && !indexUiState.range?.weekly_signature_count,
			isTrendingChecked: !!indexUiState.range?.weekly_signature_count,
		},
		actions: {
			setAllPetitions: useCallback(() => {
				clearRefine();
			}, [clearRefine]),
			setVictoriesOnly: useCallback(() => {
				clearRefine();
				filterByVictories({ isRefined: false });
			}, [filterByVictories, clearRefine]),
			setTrending: useCallback(() => {
				clearRefine();
				filterByTrending([INSTANT_SEARCH_TRENDING_FILTER_VALUE, undefined]);
			}, [clearRefine, filterByTrending]),
			trackStatusFilterClick,
		},
	};
}
